import CalendarHeader from '@/components/calendar/CalendarHeader';
import CalendarMonth from '@/components/calendar/CalendarMonth';
import { Button } from '@/components/ui/button';
import { useSearchUrlSync } from '@/hooks/search/useSearchUrlSync';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCalendarDateLogic } from '@/hooks/useCalendarDateLogic';
import { useCalendarDynamicText } from '@/hooks/useCalendarDynamicText';
import { isAfter } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';

interface PropertyCalendarProps {
  externalCheckIn?: Date | undefined;
  externalCheckOut?: Date | undefined;
  unavailableDates?: Date[];
  onDateChange?: (
    checkIn: Date | undefined,
    checkOut: Date | undefined,
  ) => void;
  onClear?: () => void;
}

const PropertyCalendar = ({
  externalCheckIn,
  externalCheckOut,
  onDateChange,
  onClear,
  unavailableDates = [],
}: PropertyCalendarProps) => {
  const isMobile = useIsMobile();

  const { replaceParams, clearParams } = useSearchUrlSync();

  const [checkInDate, setCheckInDate] = useState<Date | undefined>(undefined);
  const [checkOutDate, setCheckOutDate] = useState<Date | undefined>(undefined);
  const [isSelectingCheckOut, setIsSelectingCheckOut] = useState(false);

  const {
    currentDate,
    setCurrentDate,
    memoizedCalendarData,
    isDateUnavailable,
    isDateBooked,
    isDatePast,
    isDateSelected,
  } = useCalendarDateLogic({ unavailableDates, isMobile });

  const dynamicTexts = useCalendarDynamicText({ checkInDate, checkOutDate });

  useEffect(() => {
    setCheckInDate(externalCheckIn);
    setCheckOutDate(externalCheckOut);
    setIsSelectingCheckOut(!!externalCheckIn && !externalCheckOut);
  }, [externalCheckIn, externalCheckOut]);

  const syncUrl = useCallback(
    (ci?: Date, co?: Date) => {
      replaceParams({ checkIn: ci, checkOut: co });
    },
    [replaceParams],
  );

  const handleDateClick = useCallback(
    (date: Date) => {
      if (isDateUnavailable(date)) return;

      if (!checkInDate || checkOutDate || !isSelectingCheckOut) {
        setCheckInDate(date);
        setCheckOutDate(undefined);
        setIsSelectingCheckOut(true);
        onDateChange?.(date, undefined);
        syncUrl(date, undefined);
        return;
      }

      if (checkInDate && isSelectingCheckOut) {
        if (isAfter(date, checkInDate)) {
          setCheckOutDate(date);
          setIsSelectingCheckOut(false);
          onDateChange?.(checkInDate, date);
          syncUrl(checkInDate, date);
        } else {
          setCheckInDate(date);
          setCheckOutDate(undefined);
          setIsSelectingCheckOut(true);
          onDateChange?.(date, undefined);
          syncUrl(date, undefined);
        }
      }
    },
    [
      checkInDate,
      checkOutDate,
      isSelectingCheckOut,
      isDateUnavailable,
      onDateChange,
      syncUrl,
    ],
  );

  const onSelectionCleared = useCallback(() => {
    setCheckInDate(undefined);
    setCheckOutDate(undefined);
    setIsSelectingCheckOut(false);
    onDateChange?.(undefined, undefined);

    clearParams(['checkin', 'checkout', 'guests']);
    onClear?.();
  }, [onDateChange, onClear, clearParams]);

  const getDayClassName = useCallback(
    (day: Date, isCurrentMonth: boolean) => {
      const booked = isDateBooked(day);
      const past = isDatePast(day);
      const selectionType = isDateSelected(day, checkInDate, checkOutDate);

      let className =
        'h-10 w-10 text-sm rounded-full transition-colors relative flex items-center justify-center ';

      if (!isCurrentMonth) {
        className += 'text-gray-300 cursor-default ';
      } else if (!booked && !past) {
        className += 'hover:bg-gray-100 cursor-pointer ';
      }

      if (booked) {
        className +=
          'text-gray-400 cursor-not-allowed bg-[repeating-linear-gradient(135deg,_rgba(156,163,175,0.15)_0_10%,_transparent_10%_20%)] ';
      } else if (past) {
        className += 'text-gray-400 bg-gray-100 cursor-not-allowed ';
      }

      if (selectionType === 'start' || selectionType === 'end') {
        className += 'bg-black text-white ';
      } else if (selectionType === 'range') {
        className += 'bg-gray-200 ';
      }

      return className;
    },
    [isDateBooked, isDatePast, isDateSelected, checkInDate, checkOutDate],
  );

  return (
    <div className="py-6 border-b border-gray-200" data-calendar-section>
      <CalendarHeader
        title={dynamicTexts.title}
        subtext={dynamicTexts.subtext}
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        onSelectionCleared={onSelectionCleared}
        isMobile={isMobile}
      />

      <div className={`flex ${isMobile ? 'flex-col' : 'space-x-8'}`}>
        <CalendarMonth
          monthDate={memoizedCalendarData.currentMonth}
          days={memoizedCalendarData.currentMonthDays}
          onDateClick={handleDateClick}
          getDayClassName={getDayClassName}
          isDateUnavailable={isDateUnavailable}
        />
        {!isMobile && (
          <CalendarMonth
            monthDate={memoizedCalendarData.nextMonth}
            days={memoizedCalendarData.nextMonthDays}
            onDateClick={handleDateClick}
            getDayClassName={getDayClassName}
            isDateUnavailable={isDateUnavailable}
          />
        )}
      </div>

      {isMobile && (checkInDate || checkOutDate) && (
        <div className="mt-6 flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onSelectionCleared}
            className="text-sm"
          >
            Limpiar
          </Button>
        </div>
      )}
    </div>
  );
};

export default PropertyCalendar;
